import { FC, useState } from "react";

import { Alert, Button, Modal, Space } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import styles from "./UserItem.module.scss";

import { IUser } from "@/models/User";
import { UsersApi } from "@/api";
import { ERROR_500_MESSAGE } from "@/api/constants";
import { EditUser } from "@/modules";

const { confirm } = Modal;

interface UserItemProps {
  user: IUser;
  editUser: (user: IUser) => void;
  deleteUser: (id: number) => void;
}

const UserItem: FC<UserItemProps> = ({ user, editUser, deleteUser }) => {
  const [editModal, setEditModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const removeUser = async () => {
    try {
      setDeleteError("");
      setDeleteLoading(true);
      await UsersApi.delete(user.userId);
      deleteUser(user.userId);
    } catch (e: any) {
      setDeleteError(e.message || ERROR_500_MESSAGE);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: "Are you sure you want to delete this user?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        removeUser();
      },
    });
  };

  const handleEdit = (user: IUser) => {
    setEditModal(false);
    editUser(user);
  };

  return (
    <Space direction="vertical" className={styles.container}>
      <div className={styles.item}>
        <Space>
          <span>
            <b>login</b>: {user.login}
          </span>
          <span>
            <b>password</b>: {user.password}
          </span>
          <span>
            <b>type</b>: {user.userType}
          </span>
        </Space>
        <Space>
          <Button
            data-testid="user-edit"
            icon={<EditOutlined />}
            onClick={() => setEditModal(true)}
          />
          <Button
            data-testid="user-delete"
            disabled={deleteLoading}
            loading={deleteLoading}
            icon={<DeleteOutlined />}
            onClick={handleDelete}
          />
        </Space>
      </div>
      <Modal
        visible={editModal}
        footer={false}
        onCancel={() => setEditModal(false)}
      >
        <EditUser user={user} onEdited={handleEdit} />
      </Modal>
      {deleteError && <Alert type="error" description={deleteError} />}
    </Space>
  );
};

export default UserItem;
